// ═══════════════════════════════════════════════════════════════════
//  modules/ah/AhAnnonces.js
//  AhAnnonces
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { argent, dateCourte, erreurDe, etatInfo, horodate, nomObjet, nombre } from "./ah";
import { MessageErreur } from "./AhBoites";

// Le journal répond à « que s'est-il passé ». Cet écran répond à « où en
// sont les choses » : ce qui est en vente, ce qui a expiré, ce qu'un joueur
// a mis, ce qu'un autre a acheté. Deux questions, deux écrans.
const VIDE = { etat: "", vendeur: "", acheteur: "", objet: "", serveur: "", tri: "recent" };

const TRIS = [
  { id: "recent", label: "Les plus récentes" },
  { id: "ancien", label: "Les plus anciennes" },
  { id: "expire", label: "Expirent en premier" },
  { id: "prix", label: "Prix croissant" },
  { id: "prix_desc", label: "Prix décroissant" },
  { id: "objet", label: "Par objet" },
];

// Les raccourcis qui couvrent les questions qu'on se pose vraiment.
// « Actif » et « expiré » sont des états ; « vendu par » et « acheté par »
// sont deux champs différents, et c'est la distinction qui compte : une
// annonce a UN vendeur et peut avoir PLUSIEURS acheteurs.
const RACCOURCIS = [
  { label: "Tout", etat: "" },
  { label: "En vente", etat: "EN_VENTE" },
  { label: "Épuisées", etat: "EPUISEE" },
  { label: "Expirées", etat: "EXPIREE" },
  { label: "Retirées ou annulées", etat: "RETIREE,ANNULEE" },
];

export function AhAnnonces({ token, onOuvrirAnnonce }) {
  const [filtres, setFiltres] = useState(VIDE);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filtres).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set("page", String(page));
      const res = await fetch(`${API_BASE}/api/ah/annonces?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, filtres, page]);
  useEffect(() => { load(); }, [load]);

  const changer = (k, v) => { setPage(1); setFiltres((f) => ({ ...f, [k]: v })); };
  const actif = Object.entries(filtres).some(([k, v]) => v && k !== "tri");
  const erreur = loading && !data ? null : erreurDe(data);

  const champ = {
    background: THEME.surface, border: `1px solid ${THEME.border}`,
    color: THEME.text, borderRadius: 6, padding: "7px 10px", fontSize: 12,
  };

  const compte = (etat) => {
    if (!data?.etats) return null;
    if (!etat) return data.etats.reduce((s, e) => s + e.total, 0);
    return etat.split(",").reduce((s, e) => s + (data.etats.find((x) => x.etat === e)?.total || 0), 0);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ── Les raccourcis d'état ─────────────────────────────────── */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {RACCOURCIS.map((r) => {
          const choisi = filtres.etat === r.etat;
          const n = compte(r.etat);
          return (
            <button key={r.label} onClick={() => changer("etat", r.etat)}
              style={{
                background: choisi ? THEME.accentDim : THEME.surface,
                border: `1px solid ${choisi ? THEME.accent : THEME.border}`,
                color: choisi ? THEME.accent : THEME.textMuted,
                borderRadius: 20, padding: "5px 14px", cursor: "pointer",
                fontSize: 12, fontWeight: choisi ? 700 : 400,
              }}>
              {r.label}{n !== null && <span style={{ opacity: 0.7 }}> · {n}</span>}
            </button>
          );
        })}
      </div>

      {/* ── Les filtres ───────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input style={{ ...champ, minWidth: 160 }} placeholder="Vendu par…"
          value={filtres.vendeur} onChange={(e) => changer("vendeur", e.target.value)} />
        <input style={{ ...champ, minWidth: 160 }} placeholder="Acheté par…"
          value={filtres.acheteur} onChange={(e) => changer("acheteur", e.target.value)} />
        <input style={{ ...champ, minWidth: 150 }} placeholder="Objet (NETHER_WART…)"
          value={filtres.objet} onChange={(e) => changer("objet", e.target.value)}
          title="La recherche porte sur l'identifiant Minecraft, pas sur le nom français affiché" />
        <select style={champ} value={filtres.serveur} onChange={(e) => changer("serveur", e.target.value)}>
          <option value="">Les deux serveurs</option>
          {(data?.serveurs || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select style={champ} value={filtres.tri} onChange={(e) => changer("tri", e.target.value)}>
          {TRIS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        {actif && (
          <button onClick={() => { setPage(1); setFiltres(VIDE); }}
            style={{ ...champ, cursor: "pointer", color: THEME.textMuted }}>Effacer</button>
        )}
      </div>

      {/* ── Le total du filtre, pas de la page ────────────────────── */}
      {data && data.somme && (
        <div style={{ color: THEME.textMuted, fontSize: 12 }}>
          {loading ? "…" : (
            <>
              <strong style={{ color: THEME.text }}>{nombre(data.total)}</strong> annonce{data.total > 1 ? "s" : ""}
              {" · "}{nombre(data.somme.vendeurs)} vendeur{data.somme.vendeurs > 1 ? "s" : ""}
              {data.somme.unites > 0 && (
                <> · <strong style={{ color: THEME.gold }}>{nombre(data.somme.unites)}</strong> unités
                  encore en vente pour <strong style={{ color: THEME.gold }}>{argent(data.somme.valeur)}</strong></>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Le tableau ────────────────────────────────────────────── */}
      {loading && !data ? (
        <div style={{ color: THEME.textMuted }}>Chargement des annonces...</div>
      ) : erreur ? (
        <MessageErreur texte={erreur} />
      ) : data.lignes.length === 0 ? (
        <div style={{ color: THEME.textMuted, background: THEME.surface, borderRadius: 8, padding: 20, textAlign: "center", fontSize: 13 }}>
          {actif ? "Aucune annonce ne correspond à ces filtres." : "Aucune annonce n'a jamais été posée."}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 900 }}>
            <thead>
              <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                {["N°", "Objet", "Vendeur", "État", "Quantité", "Prix unit.", "Encaissé", "Posée le", "Expire le"].map((h) => (
                  <th key={h} style={{ padding: "7px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.lignes.map((l) => {
                const e = etatInfo(l.etat);
                return (
                  <tr key={l.id} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                    <td style={{ padding: "7px 10px", color: THEME.textDim, fontVariantNumeric: "tabular-nums" }}>{l.id}</td>
                    <td style={{ padding: "7px 10px" }}>
                      <button onClick={() => onOuvrirAnnonce(l.id)}
                        style={{ background: "none", border: "none", padding: 0, color: THEME.accent, cursor: "pointer", fontSize: 12, textAlign: "left", textDecoration: "underline dotted" }}>
                        {nomObjet(l.identifiant_lisible)}
                      </button>
                      {!l.equilibre && (
                        <span style={{ color: THEME.red, fontSize: 10, marginLeft: 6, fontWeight: 700 }}
                          title="Vendu + restant ne fait pas la quantité de départ">⛔ écart</span>
                      )}
                    </td>
                    <td style={{ padding: "7px 10px", color: THEME.text, whiteSpace: "nowrap" }}>{l.vendeur_pseudo}</td>
                    <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                      <span style={{ background: e.fond, color: e.color, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4 }}>{e.label}</span>
                    </td>
                    <td style={{ padding: "7px 10px", color: THEME.text, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {/* Trois nombres qui racontent l'histoire de l'annonce :  */}
                      {/* ce qui reste, ce qui est parti, ce qu'il y avait.      */}
                      <span style={{ color: l.quantite_restante > 0 ? THEME.text : THEME.textDim }}>{nombre(l.quantite_restante)}</span>
                      <span style={{ color: THEME.textDim }}> / {nombre(l.quantite_initiale)}</span>
                      {l.vendu > 0 && <span style={{ color: THEME.emerald, fontSize: 10 }}> · {nombre(l.vendu)} vendus</span>}
                    </td>
                    <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{argent(l.prix_unitaire)}</td>
                    <td style={{ padding: "7px 10px", color: l.encaisse > 0 ? THEME.emerald : THEME.textDim, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {l.encaisse > 0 ? argent(l.encaisse) : "—"}
                      {l.acheteurs > 1 && <span style={{ color: THEME.textDim, fontSize: 10 }}> · {l.acheteurs} ach.</span>}
                    </td>
                    <td style={{ padding: "7px 10px", color: THEME.textMuted, whiteSpace: "nowrap" }}>{dateCourte(l.posee_le)}</td>
                    <td style={{ padding: "7px 10px", color: THEME.textMuted, whiteSpace: "nowrap" }} title={horodate(l.expire_le)}>{dateCourte(l.expire_le)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.pages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            style={{ ...champ, cursor: page <= 1 ? "not-allowed" : "pointer", opacity: page <= 1 ? 0.4 : 1 }}>← Précédent</button>
          <span style={{ color: THEME.textMuted, fontSize: 12 }}>page {data.page} sur {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
            style={{ ...champ, cursor: page >= data.pages ? "not-allowed" : "pointer", opacity: page >= data.pages ? 0.4 : 1 }}>Suivant →</button>
        </div>
      )}
    </div>
  );
}
